import { Link } from "react-router-dom";
import { useCookieConsent } from "@/context/CookieConsentContext";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/over-mij", label: "Over mij" },
  { to: "/sprankelpraktijk", label: "Sprankelpraktijk" },
  { to: "/little-me-time", label: "Little me time" },
  { to: "/zomerkampen", label: "Zomerkampen" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

const Footer = () => (
  <footer className="relative bg-[hsl(25_20%_14%)] text-primary-foreground overflow-hidden">
    {/* Soft top transition — gentle warmth bleeding from content */}
    <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-accent/12 to-transparent" />
    <div className="absolute top-0 left-[15%] right-[15%] h-56 bg-[radial-gradient(ellipse_at_top,hsl(32_35%_52%_/_0.04)_0%,transparent_100%)] pointer-events-none" />

    {/* Ambient warmth — very subtle */}
    <div className="absolute top-[25%] left-[20%] w-[400px] h-[500px] rounded-full bg-[radial-gradient(ellipse_at_center,hsl(32_35%_52%_/_0.02)_0%,transparent_60%)] blur-[120px] pointer-events-none" />

    <div className="relative max-w-5xl mx-auto px-6 py-20 sm:py-32 lg:py-40">
      {/* Brand — quiet, warm presence */}
      <div className="mb-14 sm:mb-24">
        <h3 className="font-heading text-[1.85rem] sm:text-[2.3rem] italic text-primary-foreground/85 mb-4 tracking-tight leading-none">
          Sprankelien
        </h3>
        <p className="text-[14px] sm:text-[13px] text-primary-foreground/55 sm:text-primary-foreground/40 font-body leading-relaxed max-w-[300px]">
          Speltherapie, kinderyoga & zomerkampen voor kinderen in Ramsel
        </p>
        <div className="w-10 h-px bg-gradient-to-r from-accent/25 to-transparent mt-8 sm:mt-12" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-12 gap-12 sm:gap-16 md:gap-8">
        {/* Navigation */}
        <div className="md:col-span-5">
          <h4 className="font-body text-[10.5px] font-semibold uppercase tracking-[0.25em] text-primary-foreground/45 sm:text-primary-foreground/60 mb-6 sm:mb-10">
            Navigatie
          </h4>
          <nav className="grid grid-cols-2 gap-x-6 gap-y-[0.95rem]">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="text-[14px] sm:text-[13px] text-primary-foreground/65 sm:text-primary-foreground/50 hover:text-primary-foreground transition-all duration-[600ms] ease-[cubic-bezier(0.22,0.68,0.35,1)] font-body relative w-fit group min-h-[36px] flex items-center"
              >
                {link.label}
                <span className="absolute bottom-1.5 left-0 w-0 h-px bg-accent/30 group-hover:w-full transition-all duration-[600ms] ease-[cubic-bezier(0.22,0.68,0.35,1)]" />
              </Link>
            ))}
          </nav>
        </div>

        {/* Contact */}
        <div className="md:col-span-4">
          <h4 className="font-body text-[10.5px] font-semibold uppercase tracking-[0.25em] text-primary-foreground/45 sm:text-primary-foreground/60 mb-6 sm:mb-10">
            Contact
          </h4>
          <div className="space-y-5 text-[14px] sm:text-[13px] text-primary-foreground/65 sm:text-primary-foreground/50 font-body leading-relaxed">
            <p>Ramsel, 2230 Herselt<br />Provincie Antwerpen, België</p>
            <p><a href="mailto:hildenys@live.be" className="hover:text-primary-foreground transition-colors duration-[600ms] underline-offset-4 hover:underline">hildenys@live.be</a></p>
            <p><a href="tel:+32476796175" className="hover:text-primary-foreground transition-colors duration-[600ms]">+32 476 79 61 75</a></p>
          </div>
        </div>

        {/* Services summary for SEO */}
        <div className="md:col-span-3">
          <h4 className="font-body text-[10.5px] font-semibold uppercase tracking-[0.25em] text-primary-foreground/45 sm:text-primary-foreground/60 mb-6 sm:mb-10">
            Aanbod
          </h4>
          <nav className="space-y-[0.95rem]">
            <Link to="/sprankelpraktijk" className="block text-[14px] sm:text-[13px] text-primary-foreground/65 sm:text-primary-foreground/50 hover:text-primary-foreground transition-colors duration-[600ms] font-body min-h-[36px] flex items-center">
              Speltherapie & begeleiding
            </Link>
            <Link to="/little-me-time" className="block text-[14px] sm:text-[13px] text-primary-foreground/65 sm:text-primary-foreground/50 hover:text-primary-foreground transition-colors duration-[600ms] font-body min-h-[36px] flex items-center">
              Kinderyoga
            </Link>
            <Link to="/zomerkampen" className="block text-[14px] sm:text-[13px] text-primary-foreground/65 sm:text-primary-foreground/50 hover:text-primary-foreground transition-colors duration-[600ms] font-body min-h-[36px] flex items-center">
              Zomerkampen
            </Link>
            <Link to="/sfeerbeelden" className="block text-[14px] sm:text-[13px] text-primary-foreground/65 sm:text-primary-foreground/50 hover:text-primary-foreground transition-colors duration-[600ms] font-body min-h-[36px] flex items-center">
              Sfeerbeelden
            </Link>
          </nav>
        </div>
      </div>

      <div className="border-t border-primary-foreground/[0.08] mt-16 sm:mt-28 pt-8 sm:pt-10 flex flex-col sm:flex-row justify-between items-center gap-5">
        <p className="text-[11px] text-primary-foreground/35 sm:text-primary-foreground/55 font-body tracking-wide">
          © {new Date().getFullYear()} Praktijk Sprankelien
        </p>
        <div className="flex gap-6 sm:gap-8 text-[11px] text-primary-foreground/35 sm:text-primary-foreground/55 font-body">
          <Link to="/privacybeleid" className="hover:text-primary-foreground/65 transition-colors duration-[600ms] relative group">
            Privacybeleid
            <span className="absolute -bottom-px left-0 w-0 h-px bg-accent/20 group-hover:w-full transition-all duration-[600ms]" />
          </Link>
          <Link to="/algemene-voorwaarden" className="hover:text-primary-foreground/65 transition-colors duration-[600ms] relative group">
            Algemene voorwaarden
            <span className="absolute -bottom-px left-0 w-0 h-px bg-accent/20 group-hover:w-full transition-all duration-[600ms]" />
          </Link>
          <CookieSettingsLink />
        </div>
      </div>
    </div>
  </footer>
);

const CookieSettingsLink = () => {
  const { openSettings } = useCookieConsent();
  return (
    <button
      type="button"
      onClick={openSettings}
      className="hover:text-primary-foreground/65 transition-colors duration-[600ms] relative group cursor-pointer"
    >
      Cookie-voorkeuren
      <span className="absolute -bottom-px left-0 w-0 h-px bg-accent/20 group-hover:w-full transition-all duration-[600ms]" />
    </button>
  );
};

export default Footer;
